import React from 'react'
import { Button } from '@/components/ui/button'

interface EmptyStateProps {
  onCreateNote: () => void
}

export const EmptyState = ({ onCreateNote }: EmptyStateProps) => {
  // Define the CSS animation as a string to inject
  const animationStyles = `
    @keyframes fallAndStack {
      0% {
        transform: translateY(-100px) rotate(var(--rotation));
        opacity: 0;
      }
      10% {
        opacity: 1;
      }
      90% {
        transform: translateY(calc(100vh - var(--final-bottom) - 60px)) rotate(var(--rotation));
        opacity: 1;
      }
      100% {
        transform: translateY(calc(100vh - var(--final-bottom) - 60px)) rotate(var(--rotation));
        opacity: 0.8;
      }
    }
  `

  return (
    <div className="w-full flex flex-col items-center justify-center py-16 px-8 border rounded-lg bg-white dark:bg-gray-800 shadow-sm mt-8 h-[80vh] relative overflow-hidden">
      {/* Inject animation styles */}
      <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
      
      {/* Falling sticky notes animation */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Generate multiple sticky notes with different animations */}
        {Array.from({ length: 15 }).map((_, i) => {
          const delay = i * 0.8
          const duration = 8 + (i % 3) * 2
          const leftPosition = 5 + (i * 6.5) % 90
          const rotation = -15 + (i % 4) * 10
          const finalBottom = Math.floor(i / 5) * 30 // Stack in rows

          return (
            <div
              key={i}
              className="absolute w-12 h-12 opacity-0"
              style={{
                left: `${leftPosition}%`,
                animation: `fallAndStack ${duration}s ease-in ${delay}s infinite`,
                '--final-bottom': `${finalBottom}px`,
                '--rotation': `${rotation}deg`,
              } as React.CSSProperties}
            >
              <div
                className="w-full h-full bg-[#FF4A8E]/10 rounded-sm shadow-sm"
                style={{
                  transform: `rotate(${rotation}deg)`,
                  clipPath: 'polygon(0 0, 100% 0, 100% 85%, 85% 100%, 0 100%)',
                }}
              >
                {/* Sticky note fold corner */}
                <div
                  className="absolute bottom-0 right-0 w-3 h-3 bg-[#FF4A8E]/20"
                  style={{
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%)',
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {/* Notes icon */}
      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6 relative z-10">
        <svg className="w-8 h-8 text-[#FF4A8E]" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_needs_empty)">
            <path
              d="M5.00065 3.33398H15.0007C15.4427 3.33398 15.8666 3.50958 16.1792 3.82214C16.4917 4.1347 16.6673 4.55862 16.6673 5.00065V10.834H12.5007C12.0586 10.834 11.6347 11.0096 11.3221 11.3221C11.0096 11.6347 10.834 12.0586 10.834 12.5007V16.6673H5.00065C4.55862 16.6673 4.1347 16.4917 3.82214 16.1792C3.50958 15.8666 3.33398 15.4427 3.33398 15.0007V5.00065C3.33398 4.55862 3.50958 4.1347 3.82214 3.82214C4.1347 3.50958 4.55862 3.33398 5.00065 3.33398Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.6673 10.834V10.9773C16.6672 11.4193 16.4916 11.8432 16.179 12.1557L12.1557 16.179C11.8432 16.4916 11.4193 16.6672 10.9773 16.6673H10.834"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_needs_empty">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 relative z-10">
        No needs captured yet
      </h3>
      <p className="text-gray-500 dark:text-gray-400 text-center max-w-[50%] mb-6 relative z-10">
        Start capturing customer needs and insights. Add notes manually or import them from your meetings.
      </p>

      {/* <div className="flex flex-col gap-4 relative z-10">
        <Button
          onClick={onCreateNote}
          className="bg-[#FF4A8E] hover:bg-[#ff2c7d] text-white"
        >
          Add Note
        </Button>
      </div> */}
    </div>
  )
} 